import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGameModeStore, useRoomStore, type GameMode } from '@/shared/stores';
import { useMaintenanceQuery } from '@/features/maintenance';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  createRoom,
  joinRoom,
  updatePresence,
  startGame,
  startMatchmaking,
  checkMatchmakingTimeout,
  getWaitingCounts
} from '@/services/room.api';
import { getActiveGame, getGameState, type ActiveGameResponse } from '@/services/game.api';
import { useGameStore } from '@/features/game/stores/useGameStore';
import { mapServerPlayersToPlayerInfo } from '@/features/game/utils/serverPlayerMapper';
import { useRoomPresenceManager } from '@/hooks/ably';
import { useCountdownTimer } from '@/hooks/useCountdownTimer';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import { useUserProfile } from '@/features/auth/hooks/useUserProfile';
import { handleApiError, handleApiErrorWithCallback } from '@/lib/api-error-handler';
import { getAudioService } from '@/shared/services/AudioService';
import {
  GAME_CONSTANTS,
  GAME_ECONOMY,
  type GameStartingEvent,
  type GameStartEvent,
  type MatchmakingTimerStartedEvent,
  type MatchmakingTimerResetEvent,
  type MatchStartFailedEvent,
  type PlayerPresence,
  type RoomGameType
} from 'shared-types';
import { freeImg, coinImg, createImg, joinImg } from '@/assets';

/** Silent wait duration before showing "no active players" message */
const SILENT_WAIT_TIMEOUT_MS = 30000;

/**
 * Calculate remaining seconds from a matchmaking start timestamp
 */
function calculateRemainingSeconds(startedAt: string): number {
  const startTime = new Date(startedAt).getTime();
  const elapsedMs = Date.now() - startTime;
  return Math.max(0, Math.ceil(GAME_CONSTANTS.MATCHMAKING_TIMEOUT_SECONDS - elapsedMs / 1000));
}

export interface GameModeConfig {
  variant: 'blue' | 'yellow' | 'green';
  // Short text for GameModeCard
  cardTitle: string;
  cardSubtitle: string;
  cardSubtitleIcon?: string;
  // Full text for TicketCard in bottom sheet
  listTitle: string;
  listSubtitle: string;
  icon: string;
}

export const GAME_MODES: GameMode[] = ['free', 'paid', 'createRoom', 'joinRoom'];

export interface LandingPageState {
  selectedGameMode: GameMode;
  gameModeConfig: GameModeConfig;
  gameModeConfigs: Record<GameMode, GameModeConfig>;
  isBottomSheetOpen: boolean;
  isPrivateLobbyDialogOpen: boolean;
  privateLobbyGameType: RoomGameType;
  lobbyCode: string | null;
  isLobbyCodeCopied: boolean;
  isJoinRoomDialogOpen: boolean;
  invitationCode: string;
  isSettingsDialogOpen: boolean;
  // Room-related state
  gameType: RoomGameType | null;
  players: PlayerPresence[];
  countdown: number | null;
  isStarting: boolean;
  isCreator: boolean;
  roomError: string | null;
  isCreatingRoom: boolean;
  isJoiningRoom: boolean;
  // Matchmaking state
  isMatchmakingDialogOpen: boolean;
  matchmakingGameType: RoomGameType | null;
  isSearchingMatch: boolean;
  matchmakingTimeRemaining: number | null;
  shouldRefreshMatchmaking: boolean;
  // User profile data
  userPseudo: string | null;
  userPoints: number;
  userCountry: string | null;
  // Active game rejoin state
  activeGame: ActiveGameResponse | null;
  hasActiveGame: boolean;
  isRestoringGame: boolean;
  rejoinError: string | null;
  // Skin picker state
  isSkinPickerOpen: boolean;
  // New badges for skins/shop (first visit only, independent)
  showSkinsNewBadge: boolean;
  showShopNewBadge: boolean;
  // Leaderboard state
  isLeaderboardOpen: boolean;
  // Daily rewards sheet state
  isDailyRewardsOpen: boolean;
  // Insufficient points dialog state
  isInsufficientPointsDialogOpen: boolean;
  // Paid confirmation dialog state
  isPaidConfirmationDialogOpen: boolean;
  pendingPaidAction: 'matchmaking' | 'createRoom' | null;
  // Maintenance mode state
  isMaintenanceActive: boolean;
  isMaintenancePreparing: boolean;
  // Waiting players counts
  waitingCounts: { free: number; paid: number } | null;
}

interface LandingPageActions {
  openSkinPicker: () => void;
  closeSkinPicker: () => void;
  openLeaderboard: () => void;
  closeLeaderboard: () => void;
  openDailyRewards: () => void;
  closeDailyRewards: () => void;
  openGameModeSheet: () => void;
  closeGameModeSheet: () => void;
  selectGameMode: (mode: GameMode) => void;
  openPrivateLobbyDialog: () => void;
  closePrivateLobbyDialog: () => void;
  togglePrivateLobbyGameType: () => void;
  createPrivateLobby: () => void;
  copyLobbyCode: () => void;
  openJoinRoomDialog: () => void;
  closeJoinRoomDialog: () => void;
  setInvitationCode: (code: string) => void;
  joinRoom: () => void;
  startGameNow: () => void;
  getCenterButtonClickHandler: () => (() => void) | undefined;
  openSettingsDialog: () => void;
  closeSettingsDialog: () => void;
  handleRulesClick: () => void;
  handleFaqClick: () => void;
  handleDisconnect: () => void;
  rejoinGame: () => void;
  // Matchmaking actions
  closeMatchmakingDialog: () => void;
  refreshMatchmaking: () => void;
  // Insufficient points actions
  closeInsufficientPointsDialog: () => void;
  // Paid confirmation actions
  closePaidConfirmationDialog: () => void;
  confirmPaidAction: () => void;
  // External links
  openShop: () => void;
}

interface LandingPageViewModel {
  state: LandingPageState;
  actions: LandingPageActions;
}

export function useLandingPageViewModel(): LandingPageViewModel {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  // Fetch fresh profile data on mount (ensures points are up-to-date after game completion)
  const { data: profile } = useUserProfile();
  // Use numeric ID from SOKA API as userId (source of truth)
  const userId = user?.id?.toString() ?? '';
  const selectedGameMode = useGameModeStore((state) => state.selectedGameMode);
  const setSelectedGameMode = useGameModeStore((state) => state.setSelectedGameMode);
  const { isActive: isMaintenanceActive, isPreparing: isMaintenancePreparing } =
    useMaintenanceQuery();

  // Waiting players counts (polls every 5s, only while landing page is mounted)
  const { data: waitingCounts } = useQuery({
    queryKey: ['rooms', 'waiting-counts'],
    queryFn: getWaitingCounts,
    refetchInterval: 5_000,
    staleTime: 5_000
  });

  // Room store state
  const currentRoomCode = useRoomStore((state) => state.currentRoomCode);
  const creatorId = useRoomStore((state) => state.creatorId);
  const gameType = useRoomStore((state) => state.gameType);
  const players = useRoomStore((state) => state.players);
  const countdown = useRoomStore((state) => state.countdown);
  const isStarting = useRoomStore((state) => state.isStarting);
  const roomError = useRoomStore((state) => state.error);
  const setRoomCode = useRoomStore((state) => state.setRoomCode);
  const setCreatorId = useRoomStore((state) => state.setCreatorId);
  const setGameType = useRoomStore((state) => state.setGameType);
  const setIsMatchmaking = useRoomStore((state) => state.setIsMatchmaking);
  const setPlayers = useRoomStore((state) => state.setPlayers);
  const setCountdown = useRoomStore((state) => state.setCountdown);
  const setIsStarting = useRoomStore((state) => state.setIsStarting);
  const setRoomError = useRoomStore((state) => state.setError);
  const resetRoomStore = useRoomStore((state) => state.reset);

  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const [isPrivateLobbyDialogOpen, setIsPrivateLobbyDialogOpen] = useState(false);
  const [privateLobbyGameType, setPrivateLobbyGameType] = useState<RoomGameType>('free');
  const [isLobbyCodeCopied, setIsLobbyCodeCopied] = useState(false);
  const [isJoinRoomDialogOpen, setIsJoinRoomDialogOpen] = useState(false);
  const [invitationCode, setInvitationCode] = useState('');
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);

  // Matchmaking state
  const [isMatchmakingDialogOpen, setIsMatchmakingDialogOpen] = useState(false);
  const [matchmakingGameType, setMatchmakingGameType] = useState<RoomGameType | null>(null);
  const [matchmakingTimeRemaining, setMatchmakingTimeRemaining] = useState<number | null>(null);
  const matchmakingTimeRemainingRef = useRef(matchmakingTimeRemaining);
  const [shouldRefreshMatchmaking, setShouldRefreshMatchmaking] = useState(false);

  // Active game rejoin state
  const [activeGame, setActiveGame] = useState<ActiveGameResponse | null>(null);
  const [isRestoringGame, setIsRestoringGame] = useState(false);
  const [rejoinError, setRejoinError] = useState<string | null>(null);

  // Skin picker state
  const [isSkinPickerOpen, setIsSkinPickerOpen] = useState(false);

  // New badge states (shown only on first visit, dismissed independently)
  const [showSkinsNewBadge, setShowSkinsNewBadge] = useState(() => {
    return localStorage.getItem('ludoka-skins-new-badge-seen') !== 'true';
  });
  const [showShopNewBadge, setShowShopNewBadge] = useState(() => {
    return localStorage.getItem('ludoka-shop-new-badge-seen') !== 'true';
  });

  const dismissSkinsNewBadge = useCallback(() => {
    setShowSkinsNewBadge(false);
    localStorage.setItem('ludoka-skins-new-badge-seen', 'true');
  }, []);
  const dismissShopNewBadge = useCallback(() => {
    setShowShopNewBadge(false);
    localStorage.setItem('ludoka-shop-new-badge-seen', 'true');
  }, []);

  // Leaderboard state
  const [isLeaderboardOpen, setIsLeaderboardOpen] = useState(false);

  // Daily rewards sheet state
  const [isDailyRewardsOpen, setIsDailyRewardsOpen] = useState(false);

  // Insufficient points dialog state
  const [isInsufficientPointsDialogOpen, setIsInsufficientPointsDialogOpen] = useState(false);

  // Paid confirmation dialog state
  const [isPaidConfirmationDialogOpen, setIsPaidConfirmationDialogOpen] = useState(false);
  const [pendingPaidAction, setPendingPaidAction] = useState<'matchmaking' | 'createRoom' | null>(
    null
  );

  // Get game store actions for state restoration on rejoin
  const setGameId = useGameStore((state) => state.setGameId);
  const setCurrentTurn = useGameStore((state) => state.setCurrentTurn);
  const syncPawnPositionsFromServer = useGameStore((state) => state.syncPawnPositionsFromServer);
  const setAutomoveMode = useGameStore((state) => state.setAutomoveMode);
  const setTurnStarted = useGameStore((state) => state.setTurnStarted);
  const initializePlayers = useGameStore((state) => state.initializePlayers);

  // Track if gamefind SFX has been played for this match
  const gamefindPlayedRef = useRef(false);

  // Debounce timer for updatePresence API calls during reconnection storms
  const presenceUpdateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track silent wait timer when player count drops below minimum
  const silentWaitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset gamefind played flag when room code changes
  useEffect(() => {
    gamefindPlayedRef.current = false;
  }, [currentRoomCode]);

  // Cleanup debounce timer when room changes or component unmounts
  useEffect(() => {
    return () => {
      if (presenceUpdateTimerRef.current) {
        clearTimeout(presenceUpdateTimerRef.current);
      }
    };
  }, [currentRoomCode]);

  // Keep matchmaking ref in sync for stable callbacks
  useEffect(() => {
    matchmakingTimeRemainingRef.current = matchmakingTimeRemaining;
  }, [matchmakingTimeRemaining]);

  // Game mode configs with translations - computed inside hook to access t()
  const gameModeConfigs = useMemo<Record<GameMode, GameModeConfig>>(
    () => ({
      free: {
        variant: 'blue',
        cardTitle: t('gameModes.free.cardTitle'),
        cardSubtitle: t('gameModes.free.cardSubtitle'),
        listTitle: t('gameModes.free.listTitle'),
        listSubtitle: t('gameModes.free.listSubtitle'),
        icon: freeImg
      },
      paid: {
        variant: 'yellow',
        cardTitle: t('gameModes.paid.cardTitle'),
        cardSubtitle: t('gameModes.paid.cardSubtitle'),
        cardSubtitleIcon: coinImg,
        listTitle: t('gameModes.paid.listTitle'),
        listSubtitle: t('gameModes.paid.listSubtitle'),
        icon: coinImg
      },
      createRoom: {
        variant: 'green',
        cardTitle: t('gameModes.createRoom.cardTitle'),
        cardSubtitle: t('gameModes.createRoom.cardSubtitle'),
        listTitle: t('gameModes.createRoom.listTitle'),
        listSubtitle: t('gameModes.createRoom.listSubtitle'),
        icon: createImg
      },
      joinRoom: {
        variant: 'green',
        cardTitle: t('gameModes.joinRoom.cardTitle'),
        cardSubtitle: t('gameModes.joinRoom.cardSubtitle'),
        listTitle: t('gameModes.joinRoom.listTitle'),
        listSubtitle: t('gameModes.joinRoom.listSubtitle'),
        icon: joinImg
      }
    }),
    [t]
  );

  const gameModeConfig = gameModeConfigs[selectedGameMode];

  // Consolidated Ably room presence management
  const { memberCount, presenceMembers } = useRoomPresenceManager({
    roomCode: currentRoomCode,
    userId,
    username: user?.pseudo ?? undefined,
    skip: !currentRoomCode,
    onPresenceChange: useCallback(
      (members: PlayerPresence[]) => {
        // Store players in room store immediately for responsive UI
        setPlayers(members);

        // Debounce the API call to batch rapid presence events during reconnection storms
        if (currentRoomCode) {
          if (presenceUpdateTimerRef.current) {
            clearTimeout(presenceUpdateTimerRef.current);
          }
          presenceUpdateTimerRef.current = setTimeout(() => {
            presenceUpdateTimerRef.current = null;
            void updatePresence(currentRoomCode, members.length)
              .then((response) => {
                // If matchmaking timer has started and we haven't initialized it yet,
                // initialize from the room data (handles case where we missed the event)
                if (
                  response.room.matchmakingStartedAt &&
                  response.room.isMatchmaking &&
                  matchmakingTimeRemainingRef.current === null
                ) {
                  const remainingSeconds = calculateRemainingSeconds(
                    response.room.matchmakingStartedAt
                  );
                  if (remainingSeconds > 0) {
                    setMatchmakingTimeRemaining(remainingSeconds);
                    setShouldRefreshMatchmaking(false);
                  }
                }
              })
              .catch((error: unknown) => {
                handleApiError('update presence', error);
              });
          }, 500);
        }
      },
      [currentRoomCode, setPlayers]
    ),
    onGameStarting: useCallback(
      (event: GameStartingEvent) => {
        // Server-authoritative countdown start
        const elapsedMs = Date.now() - event.startedAt;
        const remainingSeconds = Math.max(0, Math.ceil(event.countdownSeconds - elapsedMs / 1000));

        // Clear matchmaking timer to prevent race condition where both timers
        // could trigger startGame simultaneously (causing SOKA API 409 conflict)
        setMatchmakingTimeRemaining(null);

        if (remainingSeconds > 0) {
          setIsStarting(true);
          setCountdown(remainingSeconds);
        } else {
          // Countdown already finished - navigate immediately
          void navigate('/games');
        }
      },
      [setIsStarting, setCountdown, navigate]
    ),
    onGameStart: useCallback(
      (_event: GameStartEvent) => {
        // Play gamefind SFX when game starts (4 players ready)
        // Only play once per match start
        if (!gamefindPlayedRef.current) {
          gamefindPlayedRef.current = true;
          getAudioService().playSFX('gamefind');
        }
        // Game started by host - navigate all players
        void navigate('/games');
      },
      [navigate]
    ),
    onMatchmakingTimerStarted: useCallback((event: MatchmakingTimerStartedEvent) => {
      // Cancel any pending silent wait timer - a new player joined
      if (silentWaitTimerRef.current) {
        clearTimeout(silentWaitTimerRef.current);
        silentWaitTimerRef.current = null;
      }

      // Calculate remaining time based on when timer started
      const startedAt = new Date(event.startedAt).getTime();
      const elapsedMs = Date.now() - startedAt;
      const remainingSeconds = Math.max(0, Math.ceil(event.timeoutSeconds - elapsedMs / 1000));
      setMatchmakingTimeRemaining(remainingSeconds);
      setShouldRefreshMatchmaking(false);
    }, []),
    onMatchmakingTimerReset: useCallback((_event: MatchmakingTimerResetEvent) => {
      // Timer was reset because player count dropped below minimum
      // Reset the timer state but wait silently for 30s before showing refresh prompt
      setMatchmakingTimeRemaining(null);
      setShouldRefreshMatchmaking(false);

      // Clear any existing silent wait timer
      if (silentWaitTimerRef.current) {
        clearTimeout(silentWaitTimerRef.current);
      }

      // Start silent wait before showing refresh prompt
      silentWaitTimerRef.current = setTimeout(() => {
        setShouldRefreshMatchmaking(true);
        silentWaitTimerRef.current = null;
      }, SILENT_WAIT_TIMEOUT_MS);
    }, []),
    onMatchStartFailed: useCallback(
      (_event: MatchStartFailedEvent) => {
        // Paid match aborted because SOKA refused to record it (Story 01-04).
        // Cancel any starting/countdown state and surface a generic error.
        setIsStarting(false);
        setCountdown(0);
        setMatchmakingTimeRemaining(null);
        setRoomError(t('lobby.errors.matchStartFailed'));
      },
      [setIsStarting, setCountdown, setRoomError, t]
    )
  });

  // React Query mutations
  const createRoomMutation = useMutation({
    mutationFn: createRoom,
    onSuccess: (data) => {
      setRoomCode(data.room.code);
      setCreatorId(data.room.creatorId);
      setGameType(data.room.gameType);
      setIsMatchmaking(false); // Private rooms are not matchmaking
      setRoomError(null);
    },
    onError: (error: Error) => {
      setRoomError(error.message);
      // Keep dialog open so user can see the error message
    }
  });

  const joinRoomMutation = useMutation({
    mutationFn: joinRoom,
    onSuccess: (data) => {
      setRoomCode(data.room.code);
      setCreatorId(data.room.creatorId);
      setGameType(data.room.gameType);
      setIsMatchmaking(data.room.isMatchmaking ?? false);
      setRoomError(null);
      // Don't close the dialog - keep it open to show player count
    },
    onError: (error: Error) => {
      setRoomError(error.message);
    }
  });

  const matchmakingMutation = useMutation({
    mutationFn: startMatchmaking,
    onSuccess: (data) => {
      setRoomCode(data.room.code);
      setCreatorId(data.room.creatorId);
      setGameType(data.room.gameType);
      setIsMatchmaking(true); // Always matchmaking
      setRoomError(null);

      // If joining an existing matchmaking room where timer already started,
      // initialize the timer immediately
      if (data.room.matchmakingStartedAt && data.room.isMatchmaking) {
        const remainingSeconds = calculateRemainingSeconds(data.room.matchmakingStartedAt);
        if (remainingSeconds > 0) {
          setMatchmakingTimeRemaining(remainingSeconds);
        }
      }
    },
    onError: (error: Error) => {
      setRoomError(error.message);
    }
  });

  // Countdown timer logic - extracted to custom hook for testability
  useCountdownTimer({
    countdown,
    onComplete: useCallback(() => {
      // When countdown finishes, someone must call startGame() to initialize game state
      // Backend will then publish game-start event that triggers navigation for ALL players
      const isCreator = creatorId === userId;

      // For matchmaking rooms: any player can start (no host concept, backend allows it)
      // For private rooms: only creator can start
      const canStart = isMatchmakingDialogOpen || isCreator;

      if (canStart && currentRoomCode) {
        void startGame(currentRoomCode).catch((error: unknown) => {
          // In matchmaking, 409 INVALID_STATUS is expected when another player starts first
          // The game-start event will trigger navigation, so suppress this error
          const apiError = error as { statusCode?: number };
          if (isMatchmakingDialogOpen && apiError.statusCode === 409) {
            return;
          }
          handleApiErrorWithCallback('start game', error, setRoomError);
        });
      }
      // Players who can't start will navigate when they receive game-start event
    }, [creatorId, userId, currentRoomCode, setRoomError, isMatchmakingDialogOpen]),
    setCountdown,
    setIsStarting
  });

  // Safety timeout: If room is full but countdown never starts, show error after 30s
  useEffect(() => {
    if (memberCount !== 4 || isStarting || countdown !== null || !currentRoomCode) {
      return;
    }

    const timeoutId = setTimeout(() => {
      // Countdown event didn't arrive - show error or auto-navigate
      console.warn('Countdown event not received after 30s, attempting manual start');
      setIsStarting(true);
      setCountdown(3); // Start a short 3-second countdown as fallback
    }, 30000); // 30 seconds timeout

    return () => {
      clearTimeout(timeoutId);
    };
  }, [memberCount, isStarting, countdown, currentRoomCode, setIsStarting, setCountdown]);

  // Matchmaking timer countdown effect
  // This effect only runs for matchmaking rooms (matchmakingTimeRemaining is only set for matchmaking)
  useEffect(() => {
    if (matchmakingTimeRemaining === null || matchmakingTimeRemaining <= 0) {
      return;
    }

    const intervalId = setInterval(() => {
      setMatchmakingTimeRemaining((prev) => {
        if (prev === null || prev <= 1) {
          // Timer reached 0 - check with backend what to do
          if (currentRoomCode) {
            void checkMatchmakingTimeout(currentRoomCode).then((result) => {
              if (result.shouldStart) {
                // For matchmaking, any player can start (backend allows it)
                void startGame(currentRoomCode).catch((error: unknown) => {
                  // 409 INVALID_STATUS is expected when another player starts first
                  // The game-start event will trigger navigation, so suppress this error
                  const apiError = error as { statusCode?: number };
                  if (apiError.statusCode === 409) {
                    return;
                  }
                  handleApiErrorWithCallback('auto-start game', error, setRoomError);
                });
              } else if (result.shouldRefresh) {
                // Show refresh prompt
                setShouldRefreshMatchmaking(true);
              }
            });
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, [matchmakingTimeRemaining, currentRoomCode, setRoomError]);

  const openGameModeSheet = useCallback(() => {
    setIsBottomSheetOpen(true);
  }, []);

  const closeGameModeSheet = useCallback(() => {
    setIsBottomSheetOpen(false);
  }, []);

  // Helper to check if user has enough points for paid games
  // Prefer fresh query data over stale Zustand store
  const hasEnoughPointsForPaidGame = useCallback(() => {
    return (profile?.points ?? user?.points ?? 0) >= GAME_ECONOMY.PAID_ENTRY_FEE;
  }, [profile?.points, user?.points]);

  // Paid confirmation dialog actions - must be declared before selectGameMode/createPrivateLobby
  const openPaidConfirmationDialog = useCallback((action: 'matchmaking' | 'createRoom') => {
    setPendingPaidAction(action);
    setIsPaidConfirmationDialogOpen(true);
  }, []);

  const closePaidConfirmationDialog = useCallback(() => {
    setIsPaidConfirmationDialogOpen(false);
    setPendingPaidAction(null);
  }, []);

  const confirmPaidAction = useCallback(() => {
    // Check maintenance status before proceeding
    if (isMaintenancePreparing) {
      setIsPaidConfirmationDialogOpen(false);
      setPendingPaidAction(null);
      setRoomError(t('maintenance.cannotCreateGame'));
      return;
    }

    const action = pendingPaidAction;
    // Close dialog first
    setIsPaidConfirmationDialogOpen(false);
    setPendingPaidAction(null);

    if (action === 'matchmaking') {
      // Start paid matchmaking
      setMatchmakingGameType('paid');
      setIsMatchmakingDialogOpen(true);
      matchmakingMutation.mutate('paid');
    } else if (action === 'createRoom') {
      // Create paid private room
      createRoomMutation.mutate('paid');
    }
  }, [
    pendingPaidAction,
    matchmakingMutation,
    createRoomMutation,
    isMaintenancePreparing,
    setRoomError,
    t
  ]);

  const selectGameMode = useCallback(
    (mode: GameMode) => {
      setSelectedGameMode(mode);
      setIsBottomSheetOpen(false);

      // Block new game creation when maintenance is preparing
      if (isMaintenancePreparing && (mode === 'free' || mode === 'paid' || mode === 'createRoom')) {
        setRoomError(t('maintenance.cannotCreateGame'));
        return;
      }

      switch (mode) {
        case 'createRoom':
          setIsPrivateLobbyDialogOpen(true);
          // Don't create room yet - wait for user to choose game type and click "Create"
          break;
        case 'joinRoom':
          setIsJoinRoomDialogOpen(true);
          break;
        case 'free':
          // Start matchmaking for free mode
          setMatchmakingGameType(mode);
          setIsMatchmakingDialogOpen(true);
          matchmakingMutation.mutate(mode);
          break;
        case 'paid':
          // Check points before starting paid matchmaking
          if (!hasEnoughPointsForPaidGame()) {
            setIsInsufficientPointsDialogOpen(true);
            return;
          }
          // Show confirmation dialog before starting paid matchmaking
          openPaidConfirmationDialog('matchmaking');
          break;
      }
    },
    [
      setSelectedGameMode,
      matchmakingMutation,
      hasEnoughPointsForPaidGame,
      openPaidConfirmationDialog,
      isMaintenancePreparing,
      setRoomError,
      t
    ]
  );

  const openPrivateLobbyDialog = useCallback(() => {
    setIsPrivateLobbyDialogOpen(true);
    // Don't create room yet - wait for user to choose game type and click "Create"
  }, []);

  const createPrivateLobby = useCallback(() => {
    // Check maintenance status before creating room
    if (isMaintenancePreparing) {
      setRoomError(t('maintenance.cannotCreateGame'));
      return;
    }

    if (privateLobbyGameType === 'paid') {
      // Show confirmation dialog for paid rooms
      openPaidConfirmationDialog('createRoom');
    } else {
      createRoomMutation.mutate(privateLobbyGameType);
    }
  }, [
    createRoomMutation,
    privateLobbyGameType,
    openPaidConfirmationDialog,
    isMaintenancePreparing,
    setRoomError,
    t
  ]);

  const closeDialogWithRoomCleanup = useCallback(
    (additionalCleanup?: () => void) => {
      // Notify backend when last human leaves so Redis playerCount stays accurate.
      // Bots are in real Ably presence (story 04-06), so memberCount includes them —
      // use humanMemberCount to avoid a false "room still has players" reading.
      const humanMemberCount = presenceMembers.filter((m) => !m.isBot).length;
      if (currentRoomCode && humanMemberCount <= 1) {
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        void updatePresence(currentRoomCode, 0).catch(() => {});
      }
      // Always reset room state when closing dialogs
      resetRoomStore();
      // Run any additional dialog-specific cleanup
      if (additionalCleanup) {
        additionalCleanup();
      }
    },
    [resetRoomStore, currentRoomCode, presenceMembers]
  );

  const closePrivateLobbyDialog = useCallback(() => {
    closeDialogWithRoomCleanup(() => {
      setIsPrivateLobbyDialogOpen(false);
      setIsLobbyCodeCopied(false);
      setPrivateLobbyGameType('free');
    });
  }, [closeDialogWithRoomCleanup]);

  const togglePrivateLobbyGameType = useCallback(() => {
    setPrivateLobbyGameType((prev) => {
      // If toggling to paid, check if user has enough points
      if (prev === 'free') {
        if (!hasEnoughPointsForPaidGame()) {
          setIsInsufficientPointsDialogOpen(true);
          return 'free'; // Keep as free
        }
        return 'paid';
      }
      return 'free';
    });
  }, [hasEnoughPointsForPaidGame]);

  const copyLobbyCode = useCallback(async () => {
    if (currentRoomCode) {
      try {
        await navigator.clipboard.writeText(currentRoomCode);
        setIsLobbyCodeCopied(true);
      } catch (error) {
        console.error('Failed to copy lobby code:', error);
      }
    }
  }, [currentRoomCode]);

  const openJoinRoomDialog = useCallback(() => {
    setIsJoinRoomDialogOpen(true);
  }, []);

  const closeJoinRoomDialog = useCallback(() => {
    closeDialogWithRoomCleanup(() => {
      setIsJoinRoomDialogOpen(false);
      setInvitationCode('');
      setRoomError(null);
    });
  }, [closeDialogWithRoomCleanup, setRoomError]);

  const closeMatchmakingDialog = useCallback(() => {
    // Clear silent wait timer on dialog close
    if (silentWaitTimerRef.current) {
      clearTimeout(silentWaitTimerRef.current);
      silentWaitTimerRef.current = null;
    }

    closeDialogWithRoomCleanup(() => {
      setIsMatchmakingDialogOpen(false);
      setMatchmakingGameType(null);
      setMatchmakingTimeRemaining(null);
      setShouldRefreshMatchmaking(false);
    });
  }, [closeDialogWithRoomCleanup]);

  const refreshMatchmaking = useCallback(() => {
    // Check maintenance status before retrying matchmaking
    if (isMaintenancePreparing) {
      closeMatchmakingDialog();
      setRoomError(t('maintenance.cannotCreateGame'));
      return;
    }

    // Clear silent wait timer on refresh
    if (silentWaitTimerRef.current) {
      clearTimeout(silentWaitTimerRef.current);
      silentWaitTimerRef.current = null;
    }

    // Reset state and retry matchmaking
    setShouldRefreshMatchmaking(false);
    setMatchmakingTimeRemaining(null);
    resetRoomStore();

    if (matchmakingGameType) {
      matchmakingMutation.mutate(matchmakingGameType);
    }
  }, [
    matchmakingGameType,
    matchmakingMutation,
    resetRoomStore,
    isMaintenancePreparing,
    setRoomError,
    t,
    closeMatchmakingDialog
  ]);

  const closeInsufficientPointsDialog = useCallback(() => {
    setIsInsufficientPointsDialogOpen(false);
  }, []);

  const handleJoinRoom = useCallback(() => {
    // Check maintenance status before joining room
    if (isMaintenancePreparing) {
      setRoomError(t('maintenance.cannotCreateGame'));
      return;
    }

    if (invitationCode.length === 6) {
      joinRoomMutation.mutate(invitationCode.toUpperCase());
    }
  }, [invitationCode, joinRoomMutation, isMaintenancePreparing, setRoomError, t]);

  const startGameNow = useCallback(() => {
    // Call backend to start game for all users via Ably event
    if (currentRoomCode) {
      void startGame(currentRoomCode).catch((error: unknown) => {
        handleApiErrorWithCallback('start game', error, setRoomError);
      });
    }
  }, [currentRoomCode, setRoomError]);

  const getCenterButtonClickHandler = useCallback(() => {
    // Block new game creation when maintenance is preparing
    const handleMaintenanceBlock = () => {
      setRoomError(t('maintenance.cannotCreateGame'));
    };

    switch (selectedGameMode) {
      case 'createRoom':
        return isMaintenancePreparing ? handleMaintenanceBlock : openPrivateLobbyDialog;
      case 'joinRoom':
        return openJoinRoomDialog;
      case 'free':
        return isMaintenancePreparing
          ? handleMaintenanceBlock
          : () => {
              setMatchmakingGameType(selectedGameMode);
              setIsMatchmakingDialogOpen(true);
              matchmakingMutation.mutate(selectedGameMode);
            };
      case 'paid':
        return isMaintenancePreparing
          ? handleMaintenanceBlock
          : () => {
              // Check points before starting paid matchmaking
              if (!hasEnoughPointsForPaidGame()) {
                setIsInsufficientPointsDialogOpen(true);
                return;
              }
              // Show confirmation dialog before starting paid matchmaking
              openPaidConfirmationDialog('matchmaking');
            };
      default:
        return undefined;
    }
  }, [
    selectedGameMode,
    openPrivateLobbyDialog,
    openJoinRoomDialog,
    matchmakingMutation,
    hasEnoughPointsForPaidGame,
    openPaidConfirmationDialog,
    isMaintenancePreparing,
    setRoomError,
    t
  ]);

  const openSkinPicker = useCallback(() => {
    setIsSkinPickerOpen(true);
    dismissSkinsNewBadge();
  }, [dismissSkinsNewBadge]);

  const closeSkinPicker = useCallback(() => {
    setIsSkinPickerOpen(false);
  }, []);

  const openLeaderboard = useCallback(() => {
    setIsLeaderboardOpen(true);
  }, []);

  const closeLeaderboard = useCallback(() => {
    setIsLeaderboardOpen(false);
  }, []);

  const openDailyRewards = useCallback(() => {
    setIsDailyRewardsOpen(true);
  }, []);

  const closeDailyRewards = useCallback(() => {
    setIsDailyRewardsOpen(false);
  }, []);

  const openSettingsDialog = useCallback(() => {
    setIsSettingsDialogOpen(true);
  }, []);

  const closeSettingsDialog = useCallback(() => {
    setIsSettingsDialogOpen(false);
  }, []);

  const handleRulesClick = useCallback(() => {
    // TODO: Navigate to rules page or open rules dialog
  }, []);

  const handleFaqClick = useCallback(() => {
    // TODO: Navigate to FAQ page or open FAQ dialog
  }, []);

  const handleDisconnect = useCallback(() => {
    // Clear room state
    resetRoomStore();
    // Close settings dialog
    setIsSettingsDialogOpen(false);
    // Logout from Web3Auth and SOKA backend
    void logout();
  }, [resetRoomStore, logout]);

  // Check for active game on mount (for rejoin functionality)
  useEffect(() => {
    async function checkActiveGame() {
      if (!userId) return;

      try {
        const result = await getActiveGame();
        if (result) {
          setActiveGame(result);
        }
      } catch (error) {
        console.error('Failed to check active game:', error);
      }
    }

    void checkActiveGame();
  }, [userId]);

  // Full state restore on rejoin
  const handleRejoinGame = useCallback(async () => {
    if (!activeGame) return;

    setIsRestoringGame(true);
    setRejoinError(null); // Clear any previous errors
    try {
      // 1. Fetch fresh game state from backend
      const gameState = await getGameState(activeGame.gameId);

      // 2. Set gameId first (triggers store reset if different)
      setGameId(activeGame.gameId);

      // 3. Initialize players from game state playerOrder
      const mappedPlayers = mapServerPlayersToPlayerInfo(
        gameState.playerOrder,
        gameState.pawnPositions
      );
      initializePlayers(mappedPlayers);

      // 4. Sync pawn positions
      syncPawnPositionsFromServer(gameState.pawnPositions);

      // 5. Set current turn
      setCurrentTurn(gameState.currentTurnPlayerId, gameState.currentTurnColor);

      // 6. Set turn timer if available
      if (gameState.turnStartedAt) {
        setTurnStarted(gameState.turnStartedAt, gameState.turnTimeoutSeconds ?? 30);
      }

      // 7. Set automove mode
      setAutomoveMode(gameState.automovePlayers?.includes(userId) ?? false);

      // 8. Set room code for Ably subscription
      setRoomCode(activeGame.gameId);

      // 9. Navigate to game
      void navigate('/games');
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Failed to restore game state:', error);
      }
      // Show user-friendly error message
      setRejoinError(t('rejoin.error'));
      // Clear active game if restore fails (game may have ended)
      setActiveGame(null);
    } finally {
      setIsRestoringGame(false);
    }
  }, [
    activeGame,
    userId,
    navigate,
    setGameId,
    initializePlayers,
    syncPawnPositionsFromServer,
    setCurrentTurn,
    setTurnStarted,
    setAutomoveMode,
    setRoomCode,
    t
  ]);

  useEffect(() => {
    if (!isLobbyCodeCopied) return;

    const timeoutId = setTimeout(() => {
      setIsLobbyCodeCopied(false);
    }, 2000);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [isLobbyCodeCopied]);

  const state: LandingPageState = {
    selectedGameMode,
    gameModeConfig,
    gameModeConfigs,
    isBottomSheetOpen,
    isPrivateLobbyDialogOpen,
    privateLobbyGameType,
    lobbyCode: currentRoomCode,
    isLobbyCodeCopied,
    isJoinRoomDialogOpen,
    invitationCode,
    isSettingsDialogOpen,
    // Room-related state
    gameType,
    players,
    countdown,
    isStarting,
    isCreator: creatorId === userId,
    roomError,
    isCreatingRoom: createRoomMutation.isPending,
    isJoiningRoom: joinRoomMutation.isPending,
    // Matchmaking state
    isMatchmakingDialogOpen,
    matchmakingGameType,
    isSearchingMatch: matchmakingMutation.isPending,
    matchmakingTimeRemaining,
    shouldRefreshMatchmaking,
    // User profile data (prefer fresh query data, fallback to Zustand store)
    userPseudo: profile?.pseudo ?? user?.pseudo ?? null,
    userPoints: profile?.points ?? user?.points ?? 0,
    userCountry: profile?.country ?? user?.country ?? null,
    // Skin picker state
    isSkinPickerOpen,
    // New badge states
    showSkinsNewBadge,
    showShopNewBadge,
    // Active game rejoin state
    activeGame,
    hasActiveGame: activeGame !== null,
    isRestoringGame,
    rejoinError,
    // Leaderboard state
    isLeaderboardOpen,
    // Daily rewards sheet state
    isDailyRewardsOpen,
    // Insufficient points dialog state
    isInsufficientPointsDialogOpen,
    // Paid confirmation dialog state
    isPaidConfirmationDialogOpen,
    pendingPaidAction,
    // Maintenance mode state
    isMaintenanceActive,
    isMaintenancePreparing,
    // Waiting players counts
    waitingCounts: waitingCounts ?? null
  };

  const actions: LandingPageActions = {
    openSkinPicker,
    closeSkinPicker,
    openLeaderboard,
    closeLeaderboard,
    openDailyRewards,
    closeDailyRewards,
    openGameModeSheet,
    closeGameModeSheet,
    selectGameMode,
    openPrivateLobbyDialog,
    closePrivateLobbyDialog,
    togglePrivateLobbyGameType,
    createPrivateLobby,
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    copyLobbyCode,
    openJoinRoomDialog,
    closeJoinRoomDialog,
    setInvitationCode,
    joinRoom: handleJoinRoom,
    startGameNow,
    getCenterButtonClickHandler,
    openSettingsDialog,
    closeSettingsDialog,
    handleRulesClick,
    handleFaqClick,
    handleDisconnect,
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    rejoinGame: handleRejoinGame,
    closeMatchmakingDialog,
    refreshMatchmaking,
    closeInsufficientPointsDialog,
    closePaidConfirmationDialog,
    confirmPaidAction,
    openShop: () => {
      dismissShopNewBadge();
      const baseUrl = import.meta.env.VITE_SOKA_WEB_URL;
      if (baseUrl) {
        window.open(`${baseUrl}/shop`, '_blank');
      }
    }
  };

  return {
    state,
    actions
  };
}
